import {
  useState,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import {
  useSensor,
  useSensors,
  PointerSensor,
  TouchSensor,
  KeyboardSensor,
  closestCenter,
} from '@dnd-kit/core';
import {
  CELL_WIDTH,
  ROW_HEIGHT,
} from '../utils/gridConstants';
import { createSnapToGridModifier } from '../utils/dndModifiers';

/**
 * Custom hook for managing drag-and-drop functionality in the planner grid.
 * Uses @dnd-kit for accessible, performant drag operations.
 *
 * @param {Object} options - Configuration options
 * @param {Object} options.weekIndexMap - Map of week string to column index
 * @param {number} options.weeksLength - Total number of weeks in the grid
 * @param {number} options.maxRows - Maximum row number
 * @param {number} options.cellWidth - Width of each grid cell (default: CELL_WIDTH)
 * @param {number} options.rowHeight - Height of each row (default: ROW_HEIGHT)
 * @param {Function} options.onMoveDuringDrag - Called during drag for visual feedback
 * @param {Function} options.onMoveComplete - Called when drag ends to commit changes
 * @param {Function} options.onSelect - Called to select a block
 * @param {boolean} options.isOnline - Whether the app is online
 * @returns {Object} DnD context props and handlers
 */
const usePlannerDnd = ({
  weekIndexMap,
  weeksLength,
  maxRows,
  cellWidth = CELL_WIDTH,
  rowHeight = ROW_HEIGHT,
  onMoveDuringDrag,
  onMoveComplete,
  onSelect,
  isOnline = true,
}) => {
  // Track which block is being dragged
  const [activeBlockId, setActiveBlockId] = useState(null);
  const [activeBlockData, setActiveBlockData] = useState(null);

  // Store the starting position for comparison on drag end
  const dragStartRef = useRef(null);

  // Last row during drag (for row change detection)
  const lastDragRowRef = useRef(null);

  // Configure sensors with activation constraints to prevent accidental drags
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        // Require 8px movement before starting drag
        // This prevents accidental drags when clicking
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        // On touch devices, require 200ms delay OR 5px movement
        // This allows normal scrolling while still enabling drag
        delay: 200,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      // Default keyboard sensor - arrow keys + space/enter
      coordinateGetter: (event, { currentCoordinates }) => {
        // Custom coordinate getter for grid-based movement
        const step = event.shiftKey ? 5 : 1; // Shift for faster movement

        switch (event.code) {
          case 'ArrowRight':
            return { ...currentCoordinates, x: currentCoordinates.x + (cellWidth * step) };
          case 'ArrowLeft':
            return { ...currentCoordinates, x: currentCoordinates.x - (cellWidth * step) };
          case 'ArrowDown':
            return { ...currentCoordinates, y: currentCoordinates.y + (rowHeight * step) };
          case 'ArrowUp':
            return { ...currentCoordinates, y: currentCoordinates.y - (rowHeight * step) };
          default:
            return currentCoordinates;
        }
      },
    }),
  );

  // Create snap-to-grid modifier
  const snapToGrid = useMemo(
    () => createSnapToGridModifier(cellWidth, rowHeight),
    [cellWidth, rowHeight],
  );

  // Restrict drag to grid bounds
  const restrictToGridBounds = useMemo(
    () => ({ transform, activeNodeRect }) => {
      if (!transform || !activeNodeRect || !dragStartRef.current) {
        return transform;
      }

      const { col: startCol, row: startRow } = dragStartRef.current;
      const weekSpan = activeBlockData?.weekSpan || 1;

      // Calculate the delta in grid units
      const deltaCol = Math.round(transform.x / cellWidth);
      const deltaRow = Math.round(transform.y / rowHeight);

      // Calculate target position
      const targetCol = startCol + deltaCol;
      const targetRow = startRow + deltaRow;

      // Clamp to grid bounds
      const clampedCol = Math.max(0, Math.min(weeksLength - weekSpan, targetCol));
      const clampedRow = Math.max(1, Math.min(maxRows, targetRow));

      // Calculate clamped transform
      const clampedX = (clampedCol - startCol) * cellWidth;
      const clampedY = (clampedRow - startRow) * rowHeight;

      return {
        ...transform,
        x: clampedX,
        y: clampedY,
      };
    },
    [cellWidth, rowHeight, weeksLength, maxRows, activeBlockData],
  );

  const modifiers = useMemo(
    () => [snapToGrid, restrictToGridBounds],
    [snapToGrid, restrictToGridBounds],
  );

  /**
   * Handle drag start
   * - Store starting position
   * - Select the block
   * - Track active block
   */
  const handleDragStart = useCallback((event) => {
    const { active } = event;
    const blockData = active.data.current;

    if (!blockData || !isOnline) return;

    setActiveBlockId(active.id);
    setActiveBlockData(blockData);

    // Store starting position for comparison on drag end
    const startCol = weekIndexMap.get(blockData.block.fromWeek);
    const startRow = blockData.block.row;

    dragStartRef.current = {
      col: startCol,
      row: startRow,
      blockId: active.id,
    };

    lastDragRowRef.current = startRow;

    // Select the block
    if (onSelect) {
      onSelect(active.id);
    }
  }, [weekIndexMap, isOnline, onSelect]);

  /**
   * Handle drag move (continuous during drag)
   * - Calculate new grid position from transform
   * - Only trigger callback if row changed (for smooth visual feedback)
   */
  const handleDragMove = useCallback((event) => {
    const { active, delta } = event;

    if (!dragStartRef.current || !activeBlockData) return;

    const { col: startCol, row: startRow } = dragStartRef.current;
    const weekSpan = activeBlockData.weekSpan || 1;

    // Calculate new position from delta
    const deltaColRaw = delta.x / cellWidth;
    const deltaRowRaw = delta.y / rowHeight;

    // Round to nearest grid cell
    const deltaCol = Math.round(deltaColRaw);
    const deltaRow = Math.round(deltaRowRaw);

    // Calculate new column (clamped to bounds)
    const newCol = Math.max(
      0,
      Math.min(weeksLength - weekSpan, startCol + deltaCol),
    );

    // Calculate new row (clamped to 1..maxRows)
    const newRow = Math.max(1, Math.min(maxRows, startRow + deltaRow));

    // Only trigger callback if row changed (prevents excessive updates)
    if (newRow !== lastDragRowRef.current) {
      lastDragRowRef.current = newRow;

      if (onMoveDuringDrag) {
        onMoveDuringDrag(active.id, newCol, newRow, weekSpan);
      }
    }
  }, [activeBlockData, cellWidth, rowHeight, weeksLength, maxRows, onMoveDuringDrag]);

  // Track when drag ended to prevent click-through
  const dragEndTimeRef = useRef(0);

  /**
   * Handle drag end
   * - Calculate final position
   * - Commit to API if position changed
   * - Reset state
   */
  const handleDragEnd = useCallback((event) => {
    const { active, delta } = event;

    // Record when drag ended (used to prevent click-through)
    dragEndTimeRef.current = Date.now();

    if (!dragStartRef.current || !activeBlockData) {
      // Reset state
      setActiveBlockId(null);
      setActiveBlockData(null);
      dragStartRef.current = null;
      return;
    }

    const { col: startCol, row: startRow } = dragStartRef.current;
    const weekSpan = activeBlockData.weekSpan || 1;

    // Calculate final position from delta
    const deltaCol = Math.round(delta.x / cellWidth);
    const deltaRow = Math.round(delta.y / rowHeight);

    const newCol = Math.max(
      0,
      Math.min(weeksLength - weekSpan, startCol + deltaCol),
    );
    const newRow = Math.max(1, Math.min(maxRows, startRow + deltaRow));

    // Only commit if position actually changed
    const positionChanged = newCol !== startCol || newRow !== startRow;

    if (positionChanged && onMoveComplete) {
      onMoveComplete(active.id, newCol, newRow, weekSpan);
    }

    // Reset state
    setActiveBlockId(null);
    setActiveBlockData(null);
    dragStartRef.current = null;
    lastDragRowRef.current = null;
  }, [activeBlockData, cellWidth, rowHeight, weeksLength, maxRows, onMoveComplete]);

  /**
   * Handle drag cancel (escape key, etc.)
   */
  const handleDragCancel = useCallback(() => {
    setActiveBlockId(null);
    setActiveBlockData(null);
    dragStartRef.current = null;
    lastDragRowRef.current = null;
  }, []);

  /**
   * Check if a drag just ended recently (within threshold ms).
   * Used to prevent click events that fire immediately after drag end.
   */
  const justFinishedDragging = useCallback((thresholdMs = 100) => (
    Date.now() - dragEndTimeRef.current < thresholdMs
  ), []);

  return {
    // Props to pass to DndContext
    dndContextProps: {
      sensors,
      collisionDetection: closestCenter,
      modifiers,
      onDragStart: handleDragStart,
      onDragMove: handleDragMove,
      onDragEnd: handleDragEnd,
      onDragCancel: handleDragCancel,
    },

    // Active drag state (for visual feedback)
    activeBlockId,
    activeBlockData,
    isDragging: activeBlockId !== null,

    // Check if drag just finished (to prevent click-through)
    justFinishedDragging,
  };
};

export default usePlannerDnd;
